<script>
import VControl from "@/components/Planning/Helpers/VControl";
import Messages from "@/mixins/Messages";
import StakeholderService from "@/services/StakeholderService";
import StakeholderCategoryService from "@/services/StakeholderCategoryService";
import VStakeholderForm from "@/components/Forms/VStakeholderForm.vue";
import { EventBus } from "@/libraries/EventBus";
import VImportResultModal from "./VImportResultModal.vue";
import VInlineStakeholderEditor from "./VInlineStakeholderEditor.vue";
import VStakeholderCategoryEdit from "@/components/Forms/VStakeholderCategoryEdit";
import VStakeholderColumnsManager from "@/components/Forms/VStakeholderColumnsManager";

export default {
    props: {
        category: Object
    },
    components: {
        VControl,
        VStakeholderForm,
        VImportResultModal,
        VInlineStakeholderEditor,
        VStakeholderCategoryEdit,
        VStakeholderColumnsManager
    },
    mixins: [Messages],
    data() {
        return {
            showSection: false,
            meta: {},
            stakeholders: [],
            stakeholderColumns: [
                "name",
                "title",
                "address",
                "phone",
                "email",
                "note"
            ],
            checkedItems: [],
            checkAll: false,
            showModal: false,
            paginated: false,
            importResult: {},
            showImportModal: false,
            showEditModal: false,
            showColumnsManager: false
        };
    },
    async created() {
        this.$apiEnableWatcher();
        this.apiPaginator.sort = "id";
        this.apiPaginator.direction = "desc";

        await this.fetchData();

        EventBus.$on("stakeholder-data-tab-open", () => {
            this.showSection = true;
        });
        EventBus.$on("stakeholder-data-tab-close", () => {
            this.showSection = false;
        });

        if (this.stakeholders.length > 0) {
            this.showSection = true;
        }
    },
    beforeDestroy() {
        EventBus.$off("stakeholder-data-tab-open");
        EventBus.$off("stakeholder-data-tab-close");
    },
    computed: {
        total() {
            let total = 0;
            let number;
            for (const i in this.checkedItems) {
                number = parseInt(
                    this.stakeholders[this.checkedItems[i]].custom_number
                );
                if (!isNaN(number)) total += number;
            }
            return total;
        },
        customColumns() {
            return this.category.custom_columns || [];
        }
    },
    watch: {
        checkAll() {
            if (this.checkAll) {
                this.checkedItems = [...this.stakeholders.keys()];
            } else {
                this.checkedItems = [];
            }
        }
    },
    methods: {
        async fetchData() {
            let params = this.$buildParams();
            if (!this.paginated) {
                params["full-view"] = 1;
                delete params.page;
            }
            params["stakeholder_category_id"] = this.category.id;
            const { data } = await StakeholderService.GET(params);
            this.stakeholders = data.data;
            this.meta = data.meta;
        },
        openUploadDialog() {
            this.$refs.file.click();
        },
        /**
         * Perform the import
         * @param event
         */
        async importStakeholders(event) {
            const file = event.target.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append("file", this.$refs.file.files[0]);

            try {
                const { data } = await StakeholderCategoryService.UPLOAD_file(
                    this.category.id,
                    formData
                );
                this.importResult = data.data;
                this.showImportModal = true;
            } catch (err) {
                console.error("Upload failed:", err);
            }
        },
        closeImport() {
            this.fetchData();
            this.showImportModal = false;
            this.importResult = {};
        },
        deleteCategory() {
            this.$askDeleteQuestion(
                this.$t("stakeholders.confirm_delete"),
                async () => {
                    await StakeholderCategoryService.DELETE(this.category.id);
                    this.$emit("reload");

                    this.$notify({
                        message: this.$t("stakeholders.category_deleted"),
                        timeout: 5000,
                        icon: "far fa-check",
                        type: "success"
                    });
                }
            );
        },
        /**
         * Edit stakeholder item
         * @param context
         */
        edit(context) {
            this.context = context;
            this.showModal = true;
        },
        async closeReload() {
            this.loading = true;
            await this.fetchData();
            this.loading = false;
            this.showModal = false;
        },
        toggleFullView() {
            this.paginated = !this.paginated;
            this.fetchData();
        },
        renameCategory() {
            this.showEditModal = true;
        },
        /**
         * Event: handle renamed category
         */
        renamedCategory({ name }) {
            this.category.name = name;
            this.showEditModal = false;
        },
        /**
         * Get custom column value for a stakeholder item
         * @param {Object} item - stakeholder item
         * @param {Number} columnId - custom column id
         * @returns {String|null}
         */
        getCustomValue(item, columnId) {
            if (!item.custom_values || !item.custom_values.length) {
                return null;
            }
            const found = item.custom_values.find(
                cv => cv.stakeholder_custom_column_id === columnId
            );
            return found ? found.col_value : null;
        },
        /**
         * Handle columns updated - reload parent to get fresh category data
         */
        async columnsUpdated() {
            this.showColumnsManager = false;
            await this.fetchData();
            this.$emit("reload");
        }
    }
};
</script>

<template>
    <div>
        <div class="container-fluid">
            <div class="row card-wrapper">
                <div class="col-12">
                    <div class="d-flex align-items-center mb-3">
                        <v-control
                            :show="showSection"
                            @trigger="showSection = !showSection"
                        />
                        <h3 class="pb-0 mb-0" :data-category-id="category.id">
                            {{ category.name }}
                        </h3>
                    </div>
                    <template v-if="showSection">
                        <card body-classes="px-0">
                            <div class="mx-4 mb-3 d-flex justify-content-end">
                                <base-button
                                    @click="deleteCategory"
                                    type="danger"
                                    outline
                                    size="sm"
                                >
                                    <i class="far fa-trash"></i>
                                    {{ $t("stakeholders.delete_category") }}
                                </base-button>
                                <base-button
                                    @click="renameCategory"
                                    type="primary"
                                    outline
                                    size="sm"
                                >
                                    <i class="far fa-pencil"></i>
                                    {{ $t("stakeholders.rename_category") }}
                                </base-button>
                                <base-button
                                    @click="showColumnsManager = true"
                                    type="primary"
                                    outline
                                    size="sm"
                                >
                                    <i class="far fa-columns"></i>
                                    {{ $t("stakeholders.manage_columns") }}
                                </base-button>
                                <input
                                    @change="importStakeholders"
                                    type="file"
                                    name="file"
                                    ref="file"
                                    hidden
                                />
                                <base-dropdown
                                    :menu-on-right="true"
                                    :has-toggle="false"
                                    title-classes="btn btn-primary btn-outline-primary btn-sm"
                                    :title="$t('stakeholders.manage_data')"
                                    icon="far fa-cogs"
                                >
                                    <div
                                        class="dropdown-item"
                                        @click="openUploadDialog"
                                    >
                                        <i class="far fa-plus fa-fw"></i>
                                        {{ $t("stakeholders.import") }}
                                    </div>
                                    <a
                                        :href="
                                            `/v1/stakeholder-categories/csv-template/${category.id}`
                                        "
                                    >
                                        <div class="dropdown-item">
                                            <i
                                                class="far fa-line-columns fa-fw"
                                            ></i>

                                            {{
                                                $t(
                                                    "stakeholders.download_csv_template"
                                                )
                                            }}
                                        </div>
                                    </a>
                                    <a
                                        :href="
                                            `/v1/stakeholders/export/${category.id}`
                                        "
                                    >
                                        <div class="dropdown-item">
                                            <i
                                                class="far fa-file-csv fa-fw"
                                            ></i>

                                            {{
                                                $t("stakeholders.export_to_csv")
                                            }}
                                        </div>
                                    </a>
                                </base-dropdown>

                                <base-button
                                    v-if="false"
                                    @click="toggleFullView"
                                    type="default"
                                    outline
                                    size="sm"
                                >
                                    <template v-if="paginated"
                                        ><i class="far fa-expand-arrows-alt"></i
                                    ></template>
                                    <template v-else
                                        ><i
                                            class="far fa-compress-arrows-alt"
                                        ></i
                                    ></template>
                                </base-button>
                            </div>
                            <template v-if="stakeholders.length">
                                <!-- Table -->
                                <div class="table-stakeholders-wrapper">
                                    <v-table
                                        class="table-mg table table-sm table-stakeholders"
                                    >
                                        <v-thead>
                                            <v-tr>
                                                <template
                                                    v-if="
                                                        category.custom_number_col_active
                                                    "
                                                >
                                                    <v-th
                                                        class="tiny text-center"
                                                    >
                                                        <input
                                                            v-model="checkAll"
                                                            type="checkbox"
                                                        />
                                                    </v-th>
                                                    <v-th
                                                        class="text-center stk-custom_number"
                                                    >
                                                        {{
                                                            category.custom_number_col_name
                                                        }}
                                                    </v-th>
                                                </template>
                                                <v-th
                                                    v-for="(col,
                                                    i) in stakeholderColumns"
                                                    :key="i"
                                                    v-if="
                                                        category[
                                                            `${col}_col_active`
                                                        ]
                                                    "
                                                    :class="`stk-${col}`"
                                                >
                                                    <v-sorter
                                                        :paginator="
                                                            apiPaginator
                                                        "
                                                        :name="col"
                                                        @sort="
                                                            $handleSorting(
                                                                $event
                                                            )
                                                        "
                                                        >{{
                                                            $t(
                                                                `stakeholders.hdr_${col}`
                                                            )
                                                        }}</v-sorter
                                                    >
                                                </v-th>
                                                <template
                                                    v-if="
                                                        category.custom_string_col_active
                                                    "
                                                >
                                                    <v-th
                                                        class="stk-custom_string"
                                                    >
                                                        {{
                                                            category.custom_string_col_name
                                                        }}
                                                    </v-th>
                                                </template>
                                                <v-th
                                                    v-for="customCol in customColumns"
                                                    :key="
                                                        'hdr-cc-' + customCol.id
                                                    "
                                                    class="stk-custom_col"
                                                >
                                                    {{ customCol.name }}
                                                </v-th>
                                                <v-th
                                                    style="width: 36px"
                                                ></v-th>
                                            </v-tr>
                                        </v-thead>

                                        <v-tbody>
                                            <v-tr
                                                v-for="(item,
                                                i) in stakeholders"
                                                :key="item.id"
                                            >
                                                <template
                                                    v-if="
                                                        category.custom_number_col_active
                                                    "
                                                >
                                                    <v-td class="text-center">
                                                        <input
                                                            v-model="
                                                                checkedItems
                                                            "
                                                            type="checkbox"
                                                            :value="i"
                                                        />
                                                    </v-td>
                                                    <v-td class="text-center">
                                                        <v-inline-stakeholder-editor
                                                            v-model="
                                                                item.custom_number
                                                            "
                                                            field="custom_number"
                                                            :field-id="item.id"
                                                            @save="fetchData"
                                                        />
                                                    </v-td>
                                                </template>
                                                <v-td
                                                    v-for="(col,
                                                    j) in stakeholderColumns"
                                                    :key="j"
                                                    v-if="
                                                        category[
                                                            `${col}_col_active`
                                                        ]
                                                    "
                                                >
                                                    <v-inline-stakeholder-editor
                                                        v-model="item[col]"
                                                        :field="col"
                                                        :field-id="item.id"
                                                        @save="fetchData"
                                                    />
                                                </v-td>
                                                <template
                                                    v-if="
                                                        category.custom_string_col_active
                                                    "
                                                >
                                                    <v-td>
                                                        <v-inline-stakeholder-editor
                                                            v-model="
                                                                item.custom_string
                                                            "
                                                            field="custom_string"
                                                            :field-id="item.id"
                                                            @save="fetchData"
                                                        />
                                                    </v-td>
                                                </template>
                                                <v-td
                                                    v-for="customCol in customColumns"
                                                    :key="
                                                        'cc-' +
                                                            item.id +
                                                            '-' +
                                                            customCol.id
                                                    "
                                                >
                                                    <v-inline-stakeholder-editor
                                                        :value="
                                                            getCustomValue(
                                                                item,
                                                                customCol.id
                                                            )
                                                        "
                                                        :field-id="item.id"
                                                        :custom-column-id="
                                                            customCol.id
                                                        "
                                                        :existing-custom-values="
                                                            item.custom_values ||
                                                                []
                                                        "
                                                        @save="fetchData"
                                                    />
                                                </v-td>
                                                <v-td class="text-right">
                                                    <i
                                                        class="far fa-pencil fa-lg m-click"
                                                        @click="edit(item)"
                                                    >
                                                    </i>
                                                </v-td>
                                            </v-tr>
                                            <v-tr
                                                v-if="
                                                    category.custom_number_col_active
                                                "
                                            >
                                                <v-td class="text-center">
                                                    {{
                                                        $t(
                                                            "stakeholders.count"
                                                        )
                                                    }}:<br />
                                                    {{ checkedItems.length }}
                                                </v-td>
                                                <v-td class="text-center">
                                                    {{ $t("stakeholders.total")
                                                    }}<br />
                                                    {{ total }}
                                                </v-td>
                                                <v-td colspan="8"></v-td>
                                            </v-tr>
                                        </v-tbody>
                                    </v-table>
                                </div>
                                <!-- . Table -->
                                <div class="paginator" v-if="paginated">
                                    <base-pagination
                                        :page-count="
                                            meta.pagination.total_pages
                                        "
                                        :per-page="meta.pagination.per_page"
                                        :value="meta.pagination.current_page"
                                        @input="$handlePagination($event)"
                                    />
                                </div>
                            </template>
                            <div class="px-4" v-else>
                                <div class="mt-2 alert alert-warning">
                                    {{
                                        $t(
                                            "stakeholders.no_data_in_this_category"
                                        )
                                    }}
                                </div>
                            </div>
                            <base-button
                                type="link"
                                class="mx-3 mt-2"
                                size="sm"
                                @click="edit({ empty: true })"
                            >
                                <i class="far fa-plus"></i>
                                {{ $t("stakeholders.add") }}
                            </base-button>
                        </card>
                    </template>
                </div>
            </div>
        </div>
        <portal to="modals">
            <v-import-result-modal
                v-if="showImportModal"
                :context="importResult"
                :cols="stakeholderColumns"
                :category="category"
                @close="closeImport"
            />
            <v-stakeholder-form
                v-if="showModal"
                @close="showModal = false"
                @close-reload="closeReload"
                :context="context"
                :category="category"
            />
            <v-stakeholder-category-edit
                v-if="showEditModal"
                :stakeholder-category-id="category.id"
                @close="showEditModal = false"
                @close-renamed="renamedCategory"
            />
            <v-stakeholder-columns-manager
                v-if="showColumnsManager"
                :category="category"
                @close="showColumnsManager = false"
                @close-reload="columnsUpdated"
            />
        </portal>
    </div>
</template>

<style lang="scss">
th.tiny {
    width: 60px !important;
}

.table-stakeholders-wrapper {
    overflow-x: auto;
    width: 100%;
}

.table-stakeholders {
    min-width: max-content;
    td,
    th {
        padding: 8px !important;
    }

    .stk-name {
        width: 200px !important;
    }

    .stk-title {
        width: 110px !important;
    }

    .stk-address {
        width: 200px !important;
    }

    .stk-phone {
        width: 110px !important;
    }

    .stk-email {
        width: 110px !important;
    }

    .stk-note {
    }

    .stk-custom_number {
        width: 100px;
    }

    .stk-custom_string {
        width: 140px;
    }

    .stk-custom_col {
        width: 140px;
    }
}
</style>
